import { GradientVariation } from '@salecycle/ui-component-library';
import { tz } from 'moment-timezone';
import { createContext, Dispatch, FC, useContext, useReducer } from 'react';
import preferencesReducer, { IAction } from './preferencesReducer';

export interface PreferencesContext {
  timezone: string;
  timezoneOffset: number;
  gradientBarVariation: GradientVariation;
  searchOverlayVisible: boolean;
  selectClientModalVisible: boolean;
}

export type IDispatch = Dispatch<IAction>;

const PreferencesStateContext = createContext<PreferencesContext | undefined>(undefined);
const PreferencesDispatchContext = createContext<IDispatch | undefined>(undefined);

export const PreferencesProvider: FC = ({ children }) => {
  const initialTimezone = getTimezone();
  const isProduction = import.meta.env.VITE_ENV === 'production';

  const [state, dispatch] = useReducer(preferencesReducer, {
    timezone: initialTimezone,
    timezoneOffset: tz(initialTimezone).utcOffset(),
    gradientBarVariation: isProduction ? 'prod' : 'poc',
    searchOverlayVisible: false,
    selectClientModalVisible: false,
  });

  return (
    <PreferencesStateContext.Provider value={state}>
      <PreferencesDispatchContext.Provider value={dispatch}>{children}</PreferencesDispatchContext.Provider>
    </PreferencesStateContext.Provider>
  );
};

export function usePreferencesState() {
  const context = useContext(PreferencesStateContext);

  if (context === undefined) {
    throw new Error('usePreferencesState must be used within a PreferencesProvider');
  }
  return context;
}

export function usePreferencesDispatch() {
  const context = useContext(PreferencesDispatchContext);

  if (context === undefined) {
    throw new Error('usePreferencesDispatch must be used within a PreferencesProvider');
  }
  return context;
}

/**
 * Checks local storage for an existing timezone. If one isn't found it will
 * attempt to guess the timezone using `moment-timezone`
 */
function getTimezone(): string {
  const preferences = localStorage.getItem('my-salecycle-preferences');

  if (!!preferences) {
    const parsedPreferences = JSON.parse(preferences);
    const timezone = parsedPreferences?.timezone ?? tz.guess();

    return timezone;
  }

  return tz.guess();
}

export const updateTimezone = (dispatch: IDispatch, timezone: string) => {
  const timezoneOffset = tz(timezone).utcOffset();

  dispatch({
    type: 'updateTimezone',
    payload: {
      timezone,
      timezoneOffset,
    },
  });

  const existingPreferences = JSON.parse(localStorage.getItem('my-salecycle-preferences'));

  localStorage.setItem(
    'my-salecycle-preferences',
    JSON.stringify({
      ...existingPreferences,
      timezone,
      timezoneOffset,
    }),
  );
};

export const updateGradientBarVariation = (dispatch: IDispatch, gradientBarVariation: GradientVariation) => {
  dispatch({
    type: 'updateGradientBarVariation',
    payload: {
      gradientBarVariation,
    },
  });
};

export const resetGradientBarVariation = (dispatch: IDispatch) => {
  dispatch({
    type: 'resetGradientBarVariation',
  });
};

export const toggleSearchOverlay = (dispatch: IDispatch, searchOverlayVisible: boolean) => {
  dispatch({
    type: 'toggleSearchOverlay',
    payload: { searchOverlayVisible },
  });
};

export const toggleSelectClientModal = (dispatch: IDispatch, selectClientModalVisible: boolean) => {
  dispatch({
    type: 'toggleSelectClientModal',
    payload: { selectClientModalVisible },
  });
};

export const hideSelectClientModalAndShowSearchOverlay = (dispatch: IDispatch) => {
  dispatch({
    type: 'hideSelectClientModalAndShowSearchOverlay',
  });
};
